import axios from 'axios';
import config from '../config';
import { createLiveDataClient } from './cityExplorerLiveData';

const TOKEN_PREFIX = 'cityExplorerShareToken:';

const publicApi = axios.create({
  baseURL: `${config.apiUrl}/v2/public/city-explorer`,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: false,
});

publicApi.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const data = error.response?.data || {};
    const enhancedError = {
      message: data.message || data.error || error.message,
      status: error.response?.status,
      requiresPassword: Boolean(data.requiresPassword),
      locationName: data.locationName,
      label: data.label,
    };
    return Promise.reject(enhancedError);
  }
);

export const shareTokenStorageKey = (shareId) => `${TOKEN_PREFIX}${shareId}`;

export const readShareToken = (shareId) => {
  try {
    return sessionStorage.getItem(shareTokenStorageKey(shareId)) || '';
  } catch (_err) {
    return '';
  }
};

export const writeShareToken = (shareId, token) => {
  try {
    sessionStorage.setItem(shareTokenStorageKey(shareId), token);
  } catch (_err) {
    // Private browsing should still allow the current tab session in memory.
  }
};

export const clearShareToken = (shareId) => {
  try {
    sessionStorage.removeItem(shareTokenStorageKey(shareId));
  } catch (_err) {
    // ignore
  }
};

const shareHeaders = (shareId) => {
  const token = readShareToken(shareId);
  return token ? { 'X-Share-Token': token } : {};
};

const cityExplorerPublicShareService = {
  getShare: async (shareId) => {
    try {
      return await publicApi.get(`/shares/${encodeURIComponent(shareId)}`, {
        headers: shareHeaders(shareId),
      });
    } catch (error) {
      if (error.requiresPassword) {
        return {
          requiresPassword: true,
          locationName: error.locationName || '',
          label: error.label || '',
        };
      }
      throw error;
    }
  },

  unlock: async (shareId, password) => {
    const data = await publicApi.post(`/shares/${encodeURIComponent(shareId)}/unlock`, { password });
    if (data.shareToken) writeShareToken(shareId, data.shareToken);
    return data;
  },

  getMapUrl: async (shareId, categoryId, fileId, disposition = 'inline') => {
    return await publicApi.get(
      `/shares/${encodeURIComponent(shareId)}/maps/${encodeURIComponent(categoryId)}/${encodeURIComponent(fileId)}`,
      { params: { disposition }, headers: shareHeaders(shareId) }
    );
  },

  getResourceUrl: async (shareId, resourceId, disposition = 'inline') => {
    return await publicApi.get(
      `/shares/${encodeURIComponent(shareId)}/resources/${encodeURIComponent(resourceId)}/url`,
      { params: { disposition }, headers: shareHeaders(shareId) }
    );
  },

  // Live-map data, limited server-side to what the shared maps read.
  getData: async (shareId, endpoint, params) => {
    return await publicApi.get(`/shares/${encodeURIComponent(shareId)}/data/${endpoint}`, {
      params,
      headers: shareHeaders(shareId),
    });
  },

  // Same methods as cityExplorerService's live-data half, for live maps
  // rendered inside a share link.
  liveDataClient: (shareId) =>
    createLiveDataClient(`share:${shareId}`, (_locationId, endpoint, params) =>
      cityExplorerPublicShareService.getData(shareId, endpoint, params)
    ),
};

export default cityExplorerPublicShareService;
